import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Alert,
  Button,
  DatePicker,
  FormField,
  Input,
  Modal,
  enterDelay,
  toast,
  useListPresence,
} from "@playbookai/ui";
import { useRecordKpiObservations } from "../hooks/useRecordKpiObservations.js";
import {
  KPI_OBSERVATION_BATCH_MAX,
  type ManagedKpiReportingFrequency,
  type RecordKpiObservationInput,
} from "../api/types.js";
import { errorMessage } from "./error-message.js";
import {
  isRecordableKpiValue,
  localInputDate,
  localInputToIso,
  nowAsLocalInput,
  withLocalInputDate,
} from "./kpi-observation-value.js";
import { reportingPeriodLabel } from "./kpi-reporting-period.js";

export interface AddHistoricalValuesModalProps {
  readonly kpiId: string;
  /** Rendered beside each value input so the number being typed has a unit. */
  readonly unit: string;
  /** Drives the derived period label shown per row. Null renders no label —
   * an unscheduled KPI has no period to name. */
  readonly reportingFrequency: ManagedKpiReportingFrequency | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

interface DraftRow {
  /** Stable across re-orders so React keys survive a removal from the middle.
   * A array index would make the row below inherit the removed row's state. */
  readonly id: number;
  readonly value: string;
  readonly observedAt: string;
  /** Per FIELD, not per row: a reader who has typed a value but not yet touched
   * the date should be told about the value, and not scolded about a date they
   * have not reached. Both flip to true on submit. */
  readonly touchedValue: boolean;
  readonly touchedDate: boolean;
}

/** What is wrong with one row, field by field. `undefined` on both means the
 * row is submittable. */
interface RowErrors {
  readonly value: string | undefined;
  readonly observedAt: string | undefined;
}

let nextRowId = 0;
const newRow = (observedAt: string): DraftRow => ({
  id: ++nextRowId,
  value: "",
  observedAt,
  touchedValue: false,
  touchedDate: false,
});

/**
 * Backfill several past readings at once
 * (`POST /v1/kpis/:id/observations/batch`).
 *
 * WHY A SEPARATE SURFACE FROM `Add value`. They are different acts. Add value is
 * "here is today's figure" — one field and a button, defaulted to now. This is
 * "here is the quarter I never entered", which needs a date per row and is
 * worth the confirmation weight of a large panel. Folding the two together
 * would put a repeatable-row grid in front of the common single-reading case.
 *
 * ALL OR NOTHING, and the copy says so. The server takes the whole array in one
 * transaction, so a rejected row leaves NOTHING written — which matters here
 * more than anywhere else on this surface, because the ledger is append-only:
 * rows written by a half-finished loop could never be withdrawn, only apologised
 * for with more rows. This component therefore never falls back to looping the
 * single write.
 *
 * The derived period label per row is FEEDBACK, not a field. There is no stored
 * period — it is `observedAt` read through the KPI's frequency — so it updates
 * as the date does and is never sent.
 */
export function AddHistoricalValuesModal({
  kpiId,
  unit,
  reportingFrequency,
  open,
  onClose,
}: AddHistoricalValuesModalProps): JSX.Element {
  const firstValueRef = useRef<HTMLInputElement>(null);
  /* The Remove buttons by row id, and the add-row button: the two places focus
     can be sent when the row that owns the pressed Remove is taken away
     (see `removeRow`). Keyed by row id rather than by position, for the same
     reason the errors map is: a leaving row still holds a slot. */
  const removeButtonRefs = useRef(new Map<number, HTMLButtonElement>());
  const addRowRef = useRef<HTMLButtonElement>(null);
  const record = useRecordKpiObservations();
  // React pending state arrives after the click; retain this latch through
  // successful controlled dismissal, so a fast second press cannot append again.
  const batchInFlight = useRef(false);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [submitted, setSubmitted] = useState(false);

  // Seeded on OPEN, not on mount: the modal stays mounted between openings, so
  // a mount-time default would freeze the date at whenever the page loaded.
  // Two blank rows, because one would make the repeatability invisible.
  useEffect(() => {
    if (!open) return;
    batchInFlight.current = false;
    const now = nowAsLocalInput();
    setRows([newRow(now), newRow(now)]);
    setSubmitted(false);
  }, [open]);

  const patchRow = (id: number, patch: Partial<Omit<DraftRow, "id">>) => {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  const addRow = () => {
    setRows((current) =>
      current.length >= KPI_OBSERVATION_BATCH_MAX
        ? current
        : [...current, newRow(nowAsLocalInput())],
    );
  };

  const removeRow = (id: number) => {
    // Never below one row: an empty panel offers nothing to do and the server
    // rejects an empty array anyway.
    if (rows.length <= 1) return;
    const position = rows.findIndex((row) => row.id === id);
    if (position < 0) return;
    const remaining = rows.filter((row) => row.id !== id);
    setRows(remaining);
    /* Re-home focus NOW, while the row this button belongs to is still live.
       The row is retained for its exit and goes `inert` on the very next
       render, and inerting the focused element blurs it to <body> on its own —
       so the next Tab would restart at the top of the dialog. The row that took
       this one's place owns the same control; when only one row is left its
       Remove is disabled and cannot hold focus, so that case hands focus to the
       add-row button instead (ADR-0091). */
    const successor = remaining[Math.min(position, remaining.length - 1)];
    const target =
      (remaining.length > 1 && successor
        ? removeButtonRefs.current.get(successor.id)
        : null) ?? addRowRef.current;
    target?.focus();
  };

  /**
   * One row's faults, each attached to the FIELD that carries it.
   *
   * A date fault used to be reported under the value input, which is where a
   * reader would look for it last. `FormField` wires `aria-describedby` from the
   * field it wraps, so putting the message on the wrong field also announced it
   * against the wrong control.
   */
  const now = Date.now();
  const rowErrors = (row: DraftRow): RowErrors => {
    const trimmed = row.value.trim();
    const value = !trimmed
      ? "A value is required."
      : !isRecordableKpiValue(trimmed)
        ? "Enter a number with at most 14 digits and 4 decimal places."
        : undefined;

    const iso = row.observedAt ? localInputToIso(row.observedAt) : undefined;
    const observedAt = !row.observedAt
      ? "A measurement date is required."
      : !iso
        ? "Enter a valid measurement date."
        : // The server rejects a future instant outright — `observedAt` both
          // orders the series and selects the current value, so a mistyped year
          // would pin the KPI to a reading nothing could supersede. Caught here
          // so it is an inline message on the field that carries it rather than
          // a 400 naming an index.
          Date.parse(iso) > now
          ? "A historical value cannot be in the future."
          : undefined;

    return { value, observedAt };
  };

  /* Keyed by row id, not by position: a removed row keeps its slot while it
     collapses (below), so an index into a parallel array would hand the
     lingering row the faults of the row that took its place. */
  const errors = new Map(rows.map((row) => [row.id, rowErrors(row)] as const));
  const hasErrors = [...errors.values()].some((e) => e.value || e.observedAt);

  /* A removed row collapses its own height so the rows under it close the gap
     instead of jumping up under the pointer that just pressed Remove
     (ADR-0091). The source list stays the state above; this only decides how
     long a departed row is still drawn. */
  const presentRows = useListPresence(rows, (row) => String(row.id)).rows;
  /**
   * The button is gated ONLY on an in-flight request.
   *
   * Disabling it while a row is invalid is the pattern `packages/ui/CLAUDE.md`
   * calls out: a control disabled with no stated reason. Worse here, it made the
   * explanation unreachable — the only thing that could reveal which row was
   * wrong was the click the disabled button refused. Submitting an invalid form
   * now marks every field touched and says what it wants, exactly as
   * `CreateKpiPage` does.
   */
  const canSubmit = !record.isPending;

  const handleClose = () => {
    if (batchInFlight.current || record.isPending) return;
    onClose();
  };

  const handleSubmit = () => {
    // Reveals every fault at once, including on rows the reader never visited —
    // and on any row added AFTER this point, which is why the reveal is one flag
    // rather than a sweep that stamps each row as touched.
    setSubmitted(true);
    if (hasErrors || batchInFlight.current || record.isPending) return;
    batchInFlight.current = true;
    const observations: RecordKpiObservationInput[] = rows.map((row) => {
      const input: {
        -readonly [K in keyof RecordKpiObservationInput]?: RecordKpiObservationInput[K];
      } = { value: row.value.trim() };
      // Always sent here, unlike the single-reading form: a backfill row means
      // nothing without the date it was measured on, and an omitted
      // `observedAt` would silently be recorded as "now".
      const iso = localInputToIso(row.observedAt);
      if (iso) input.observedAt = iso;
      return input as RecordKpiObservationInput;
    });
    record.mutate(
      { kpiId, input: { observations } },
      {
        onSuccess: (batch) => {
          toast(
            `${batch.observations.length} value${batch.observations.length === 1 ? "" : "s"} recorded`,
            { tone: "healthy" },
          );
          onClose();
        },
        onError: (err) => {
          batchInFlight.current = false;
          toast(errorMessage(err, "Could not record the values."), {
            tone: "critical",
          });
        },
      },
    );
  };

  const atCap = rows.length >= KPI_OBSERVATION_BATCH_MAX;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="lg"
      initialFocusRef={firstValueRef}
      title="Add historical values"
      description="Records past readings for this KPI. They are saved together — if one row is rejected, none are saved. Earlier readings are always kept; a correction is recorded as another reading, never an edit."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={record.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={record.isPending}
          >
            {`Save ${rows.length} value${rows.length === 1 ? "" : "s"}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="space-y-3">
          {presentRows.map((presentRow, index) => {
            const row = presentRow.item;
            const rowError = errors.get(row.id);
            const iso = localInputToIso(row.observedAt);
            const period =
              iso && reportingFrequency
                ? reportingPeriodLabel(iso, reportingFrequency)
                : null;
            return (
              /* Three elements, one per job: the `<li>` owns the collapse, the
                 bare wrapper is the overflow clip `pb-exit-row` styles, and the
                 card owns the arrival. Arrival and departure cannot share an
                 element — they are one `cn()` merge group, so a class list
                 naming both would lose one of them. */
              /* A row retained for its exit is SCENERY: hidden from assistive
                 tech and out of the tab order for the 180ms it is still drawn,
                 or Tab walks a row that has already been deleted and a screen
                 reader announces its fields (ADR-0091). `inert` is toggled
                 imperatively and BOTH ways — React 18 serialises
                 `inert={false}` as the string "false", which HTML reads as
                 inert, and a row that reopens mid-exit must not be left
                 permanently unreachable. */
              <li
                key={presentRow.key}
                ref={(node) => node?.toggleAttribute("inert", !presentRow.open)}
                aria-hidden={presentRow.open ? undefined : true}
                className="pb-exit-row"
                data-motion-state={presentRow.open ? "open" : "closing"}
              >
                <div>
                  {/* Kept hand-built (not Card): a border-only, no-fill row —
                      see IntegrationExecutionApproval.tsx for the same
                      reasoning — plus the pb-enter-row motion grade and the
                      inert/aria-hidden exit wiring above sit on this exact
                      element. */}
                  <div
                    className="pb-enter-row rounded-lg border border-pb-border p-3"
                    style={enterDelay(index) as CSSProperties}
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="min-w-[8rem] flex-1">
                        <FormField
                          label={`Value ${index + 1}`}
                          required
                          // Shown as soon as this field has been left — so a reader
                          // fixes one row while filling the next rather than
                          // discovering every fault at the end — and unconditionally
                          // once Save has been pressed.
                          {...((submitted || row.touchedValue) &&
                          rowError?.value
                            ? { error: rowError.value }
                            : {})}
                        >
                          <Input
                            ref={index === 0 ? firstValueRef : undefined}
                            value={row.value}
                            onChange={(event) =>
                              patchRow(row.id, { value: event.target.value })
                            }
                            onBlur={() =>
                              patchRow(row.id, { touchedValue: true })
                            }
                            inputMode="decimal"
                            placeholder="e.g. 118.5"
                            {...(unit ? { suffix: unit } : {})}
                          />
                        </FormField>
                      </div>
                      <div className="min-w-[12rem] flex-1">
                        <FormField
                          label={`Measured at ${index + 1}`}
                          required
                          {...((submitted || row.touchedDate) &&
                          rowError?.observedAt
                            ? { error: rowError.observedAt }
                            : {})}
                        >
                          <DatePicker
                            value={localInputDate(row.observedAt)}
                            onChange={(date) =>
                              patchRow(row.id, {
                                observedAt: withLocalInputDate(
                                  row.observedAt,
                                  date,
                                ),
                                // A date edit is its own signal: the reader is
                                // acting on this field, so a fault in it is
                                // immediately relevant. Waiting for blur would hide
                                // a mistyped year behind one more interaction.
                                touchedDate: true,
                              })
                            }
                            placeholder="Pick a measurement date"
                          />
                        </FormField>
                      </div>
                      <div className="pt-6">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          iconOnly
                          ref={(node) => {
                            if (node)
                              removeButtonRefs.current.set(row.id, node);
                            else removeButtonRefs.current.delete(row.id);
                          }}
                          onClick={() => removeRow(row.id)}
                          disabled={rows.length <= 1 || record.isPending}
                          aria-label={`Remove value ${index + 1}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                    {/* Derived from the date beside it, never a field. Absent
                        for an unscheduled KPI, which genuinely has no period
                        to name. */}
                    {period ? (
                      <p className="mt-1 text-dense-1 text-pb-text-muted">
                        Reporting period: {period}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            ref={addRowRef}
            type="button"
            variant="secondary"
            size="sm"
            onClick={addRow}
            disabled={atCap || record.isPending}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add another row
          </Button>
          {/* A visible reason rather than a tooltip: a disabled control does not
              reliably fire hover, so a tooltip hides it from pointer and AT
              users alike (packages/ui/CLAUDE.md). */}
          {atCap ? (
            <span className="text-dense-1 text-pb-text-muted">
              {KPI_OBSERVATION_BATCH_MAX} values is the most one batch can
              carry. Save these, then add more.
            </span>
          ) : null}
        </div>

        {submitted && hasErrors ? (
          <Alert tone="critical" title="Fix the rows marked above">
            Nothing is saved until every row is valid — the readings are
            recorded together or not at all.
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
}
